import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import * as L from 'leaflet';

import { ContextService } from '../../services/context.service';
import * as courtGeom from '../../../assets/data/court.json';
import { StatsService } from '../../services/stats.service';
import { environment } from '../../../environments/environment';
import { Coord } from '@turf/helpers';
import transformRotate from '@turf/transform-rotate';

declare let googletag: any;

@Component({
  selector: 'app-map-base',
  templateUrl: './map-base.component.html',
  styleUrls: ['./map-base.component.scss']
})
export class MapBaseComponent implements OnInit, OnDestroy {
  COURT_CENTER = [0.0000673736, 0.0001257641];
  COURT_BOUNDS = [
    [0.0001374, 0.0002552],
    [0.0000237, 0.0000037]
  ];

  @ViewChild('map') mapElem: any;
  map: any;

  courtLayer: any;
  courtLinesLayer: any;
  courtCenterCircle: any;
  extraLinesLayer: any;

  popupLeft = 0;
  popupTop = 0;
  popupShown = false;
  scrollCallback: any;

  live = false;
  private contextSubscription: Subscription;

  constructor(
    protected statsService: StatsService,
    protected context: ContextService
  ) { }

  ngOnInit() {
    this.scrollCallback = this.onScroll.bind(this);
    window.addEventListener('touchmove', this.scrollCallback);
    this.map = new L.map(this.mapElem.nativeElement, {
      center: this.COURT_CENTER,
      zoom: 22,
      zoomSnap: .01,
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: false,
      maxBoundsViscosity: 1.0,

      // Mobile scroll fix (including iOS)
      dragging: false, // !L.Browser.mobile,
      touchZoom: false, // !L.Browser.mobile,
      doubleClickZoom: false, // !L.Browser.mobile,
      tap: false // !L.Browser.mobile
    });

    this.drawBaseMap();
    this.resetZoom();
    this.map.setMaxBounds(this.courtLayer.getBounds());

    // Listen for changes on context
    this.contextSubscription = this.context.contextChanged$.subscribe((context) => {
      this.live = context.live;
      this.refreshMap();
    });
    this.live = this.context.isLive();

    // Resize Map
    window.addEventListener('resize', this.resizeMap.bind(this));
  }

  onScroll() {
    this.closePopup(null);
  }

  isMobile() {
    const mediaQuery = `(max-width: ${environment.mobileBreakpoint})`;
    const isMobile = window.matchMedia(mediaQuery).matches;
    return isMobile;
  }

  ngOnDestroy() {
    if ( this.courtLayer ) { this.courtLayer.remove(); }
    if ( this.courtLinesLayer ) { this.courtLinesLayer.remove(); }
    if ( this.courtCenterCircle ) { this.courtCenterCircle.remove(); }
    if ( this.extraLinesLayer ) { this.extraLinesLayer.remove(); }
    if (this.map) { this.map.remove(); }
    if (this.contextSubscription) { this.contextSubscription.unsubscribe(); }
    window.removeEventListener('touchmove', this.scrollCallback);
    window.removeEventListener('resize', this.resizeMap.bind(this));
  }

  drawBaseMap() {
    this.drawCourt(courtGeom, null);
  }

  drawCourt(courtLines, extraLines) {
    const courtLinesRotated = this.rotateGeomIfNeeded(courtLines);
    const extraLinesRotated = extraLines && this.rotateGeomIfNeeded(extraLines);
    this.courtLinesLayer = L.layerGroup().addTo(this.map);
    // Main lines
    this.courtLayer = L.geoJSON(courtLinesRotated, {
      filter: (feature) => (
        feature.properties.class !== 'norender' && feature.properties.class !== 'base'
      ),
      style: function (feature) {
        return {
          weight: feature.properties.basket ? 2 : 1,
          color: feature.properties.basket ? '#1A1E29' : '#AEB1B9',
          fillColor: '#FFFFFF',
          fillOpacity: L.Browser.ie && feature.properties.class === 'background' ? 0 : 1
        };
      },
      smoothFactor: 0
    });
    this.courtLinesLayer.addLayer(this.courtLayer);
    // Basket circles
    const basketCenters = courtLinesRotated.features.filter((elem) => elem.properties.type === 'basketCenter');
    basketCenters.forEach((elem) => {
      const basket = L.circle([elem.geometry.coordinates[1], elem.geometry.coordinates[0]], {
        radius: elem.properties.radius,
        weight: 2,
        color: elem.properties.courtCenter ? '#AEB1B9' : '#1A1E29',
        fillColor: elem.properties.courtCenter ? '#AEB1B9' : '#FFF',
        fillOpacity: elem.properties.courtCenter ? 1 : 0
      });
      this.courtLinesLayer.addLayer(basket);
    });

    // Extra lines
    if (extraLinesRotated) {
      this.extraLinesLayer = L.geoJSON(extraLinesRotated, {
        style: function (feature) {
          return {
            weight: 1,
            color: '#4B505F',
            opacity: .7,
            dashArray: '1, 4'
          };
        },
        smoothFactor: 0
      });
      this.courtLinesLayer.addLayer(this.extraLinesLayer);
    }

    // Court center circle
    const courtCenterCircleFeature = courtLinesRotated.features.filter((elem) => elem.properties.courtCenterCircle);
    if (courtCenterCircleFeature.length) {
      this.courtCenterCircle = L.circle(
        [courtCenterCircleFeature[0].geometry.coordinates[1], courtCenterCircleFeature[0].geometry.coordinates[0]],
        {
          radius: courtCenterCircleFeature[0].properties.radiusCircle,
          weight: 1,
          color: '#AEB1B9',
          fillColor: '#FFF',
          fillOpacity: 0
        }
      );
      this.courtLinesLayer.addLayer(this.courtCenterCircle);
    }

    // Fix Z-index for IE/Edge
    this.courtLinesLayer.setZIndex(900);

    // this.loadAds();
  }

  zoomIn() {
    this.map.zoomIn();
  }

  zoomOut() {
    this.map.zoomOut();
  }

  resetZoom() {
    this.map.fitBounds(this.courtLayer.getBounds());
  }

  protected openPopup (ev, layerDomKey = '_icon') {
    this.popupShown = true;
    const popupRect = document.querySelector('.popup').getBoundingClientRect();
    const domNode = ev.target[layerDomKey];
    const iconPosition = domNode.getBoundingClientRect();
    const popupLeft = iconPosition.left + iconPosition.width / 2;
    const minLeft = popupRect.width / 2;
    const maxLeft = window.innerWidth - minLeft;
    const popupTop = iconPosition.top - 10;
    const halfHeight = window.innerHeight / 2;
    const popupTopMobile = ev.originalEvent.clientY < halfHeight ? window.innerHeight - 20 : popupRect.height + 20 + 52;
    this.popupLeft = this.isMobile() ? (window.innerWidth / 2) : Math.max(minLeft, Math.min(popupLeft, maxLeft));
    this.popupTop = this.isMobile() ? popupTopMobile : popupTop;
  }

  protected closePopup (e) {
    this.popupShown = false;
  }

  private loadAds() {
    const imageUrl = '/assets/img/marketing/mahou_squared.png';
    const imageBounds = this.courtCenterCircle.getBounds();
    const imageOverlay = L.imageOverlay(imageUrl, imageBounds, { className: 'ads', zIndex: 200}).addTo(this.map);
    /*
    const adDiv = document.createElement('div');
    adDiv.id = 'div-boton';
    adDiv.classList.add('ads');
    imageOverlay._image.parentElement.replaceChild(
      adDiv,
      imageOverlay._image
    );
    imageOverlay._image = adDiv;
    const value = googletag.cmd.push(function() { googletag.display(adDiv); });
    */
  }

  rotateGeomIfNeeded(geom, angle = 90, center: Coord = this.COURT_CENTER) {
    if (!this.isMobile()) {
      return geom;
    }
    return transformRotate(geom, angle, {pivot: center});
  }

  refreshMap() {
    throw new Error('Unable to use basemap directly, you must extend it');
  }

  resizeMap() {
    this.map.invalidateSize();
    this.resetZoom();
  }
}



// WEBPACK FOOTER //
// ./src/app/common/map-base/map-base.component.ts